import req from '@sportninja/common/api/request'
import {
  PAYMENT_GATE_OK,
  PAYMENT_PRE_SUBMIT_STEP,
  paymentGateFromRequestError,
  type PaymentGateResult,
} from '@sportninja/common/features/registration/paymentGate'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { useCallback, useState } from 'react'

interface UsePaymentGateParams {
  /** Registration session id — the same one every other step updates. */
  readonly sessionId?: string | null
}

interface UsePaymentGateResult {
  /**
   * Asks the API to validate the session before payment.
   * Never throws — a rejection resolves to a blocking result.
   */
  readonly verifyBeforePayment: () => Promise<PaymentGateResult>
  /** True while the validation request is in flight. */
  readonly isVerifying: boolean
}

/**
 * Guards the registration payment submission.
 *
 * A payment tab can sit open indefinitely. Both Stripe and SportsPay are
 * confirmed against the provider *directly* from the browser, so by the time
 * our API hears about a payment the money has already moved — a registration
 * that closed while the tab was idle would otherwise take the charge and then
 * fail to register anyone.
 *
 * `registration_step: payment_pre_submit` asks the server to re-validate the
 * session without advancing it, turning a post-charge failure into a
 * pre-charge block.
 */
export const usePaymentGate = ({
  sessionId,
}: UsePaymentGateParams): UsePaymentGateResult => {
  const [isVerifying, setIsVerifying] = useState(false)

  const verifyBeforePayment =
    useCallback(async (): Promise<PaymentGateResult> => {
      // No session id means there is nothing to validate against — the flow is
      // already broken, and letting a charge through would be worse.
      if (!sessionId) {
        return paymentGateFromRequestError({ status: 404 })
      }

      setIsVerifying(true)
      try {
        await req(`/registration/session/${sessionId}`, {
          method: 'PUT',
          body: JSON.stringify({
            registration_step: PAYMENT_PRE_SUBMIT_STEP,
          }),
        })
        return PAYMENT_GATE_OK
      } catch (error) {
        // Surface the API's own message, matched to how the other steps of the
        // flow report failures.
        return paymentGateFromRequestError(
          error as { status?: number; message?: string },
          getErrorMessage(error)
        )
      } finally {
        setIsVerifying(false)
      }
    }, [sessionId])

  return { verifyBeforePayment, isVerifying }
}

export type { PaymentGateResult }
